import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FloppyDisk, Plus, Trash, UserCircle, ArrowsClockwise } from "@phosphor-icons/react";

export default function TrainingAdmin() {
  const [data, setData] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [body, setBody] = useState("");
  const [grohub, setGrohub] = useState("");
  const [progress, setProgress] = useState([]);
  const [demo, setDemo] = useState(null);

  const load = async () => {
    const r = await api.get("/training");
    setData(r.data); setGrohub(r.data.grohub_url || "");
    if (!activeKey && r.data.sections[0]) { setActiveKey(r.data.sections[0].key); setBody(r.data.sections[0].body_md); }
    api.get("/training/progress").then((x) => setProgress(x.data)).catch(() => {});
    api.get("/admin/demo-rep").then((x) => setDemo(x.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pickSection = (s) => { setActiveKey(s.key); setBody(s.body_md); };
  const saveSection = async () => {
    try { await api.put(`/training/sections/${activeKey}`, { body_md: body }); toast.success("Section saved"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveGrohub = async () => {
    try { await api.put("/training/grohub-url", { url: grohub }); toast.success("GroHub URL saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const demoAction = async (act) => {
    try { const r = await api.post(`/admin/demo-rep/${act}`); toast.success(act === "load" ? "Demo rep loaded/refreshed" : "Demo rep removed"); setDemo(await api.get("/admin/demo-rep").then((x) => x.data)); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Training Management</h1>
        <p className="mt-1 text-sm text-muted-foreground">Edit rep training content, the GroHub link, and downloadable assets — no redeploy needed.</p>
      </div>

      <Tabs defaultValue="content">
        <TabsList>
          <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets">Assets</TabsTrigger>
          <TabsTrigger value="progress" data-testid="tab-progress">Rep Progress</TabsTrigger>
          <TabsTrigger value="demo" data-testid="tab-demo">Demo Rep</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-4">
          <div className="mb-5 rounded-sm border border-border bg-card p-4">
            <Label className="overline">GroHub Training URL (rep-facing external link)</Label>
            <div className="mt-2 flex gap-2">
              <Input data-testid="grohub-url-input" value={grohub} onChange={(e) => setGrohub(e.target.value)} placeholder="https://… (leave blank to show 'coming soon')" />
              <Button data-testid="grohub-url-save" onClick={saveGrohub} className="gap-1.5"><FloppyDisk size={15} /> Save</Button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
            <ul className="space-y-1">
              {data.sections.map((s) => (
                <li key={s.key}>
                  <button data-testid={`admin-section-${s.key}`} onClick={() => pickSection(s)}
                    className={`w-full rounded-sm px-3 py-2 text-left text-sm transition-colors ${activeKey === s.key ? "bg-[#102542] text-white" : "hover:bg-accent"}`}>{s.title}</button>
                </li>
              ))}
            </ul>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label className="overline">Markdown — {data.sections.find((s) => s.key === activeKey)?.title}</Label>
                <Button data-testid="save-section-btn" size="sm" onClick={saveSection} className="gap-1.5"><FloppyDisk size={15} /> Save section</Button>
              </div>
              <Textarea data-testid="section-editor" value={body} onChange={(e) => setBody(e.target.value)} className="min-h-[420px] font-mono text-xs" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="assets" className="mt-4"><AssetsAdmin assets={data.assets} reload={load} /></TabsContent>

        <TabsContent value="progress" className="mt-4">
          <div className="overflow-hidden rounded-sm border border-border">
            <table className="w-full text-sm">
              <thead className="bg-card"><tr className="border-b border-border"><th className="px-4 py-3 text-left overline">Rep</th><th className="px-4 py-3 text-right overline">Items completed</th></tr></thead>
              <tbody>
                {progress.map((p) => (
                  <tr key={p.rep_id} data-testid={`progress-${p.rep_id}`} className="border-b border-border">
                    <td className="px-4 py-2.5">{p.rep_name} {p.is_demo && <span className="ml-1 rounded-full bg-[#f5a623]/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-[#b9770f]">Demo</span>}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{p.done}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="demo" className="mt-4">
          <div className="max-w-lg rounded-sm border border-border bg-card p-5" data-testid="demo-panel">
            <div className="mb-3 flex items-center gap-2"><UserCircle size={20} className="text-[#3ab795]" /><div className="font-heading text-base font-semibold">Demo Rep</div></div>
            {demo ? (
              <div className="space-y-1 text-sm">
                <div>Present: <span className="font-mono">{String(demo.present)}</span></div>
                {demo.present && <>
                  <div>Login exists: <span className="font-mono">{String(demo.has_login)}</span></div>
                  <div>Password set via env: <span className="font-mono">{String(demo.password_set_via_env)}</span></div>
                  <div>Email: <span className="font-mono">{demo.email}</span></div>
                </>}
              </div>
            ) : <p className="text-sm text-muted-foreground">Loading…</p>}
            <p className="mt-3 text-xs text-muted-foreground">Password is set only via the <span className="font-mono">DEMO_REP_PASSWORD</span> env var at deploy — never baked into the build.</p>
            <div className="mt-4 flex gap-2">
              <Button data-testid="demo-load-btn" onClick={() => demoAction("load")} className="gap-1.5"><ArrowsClockwise size={15} /> Load / Refresh</Button>
              <Button data-testid="demo-remove-btn" variant="outline" onClick={() => demoAction("remove")} className="gap-1.5"><Trash size={15} /> Remove</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const CATS = ["Core Kit", "Outreach Templates", "Leave-Behinds", "Reference"];
const BLANK = { title: "", description: "", category: "Core Kit", file_url: "", per_rep_merge: false };

function AssetsAdmin({ assets, reload }) {
  const [draft, setDraft] = useState(BLANK);
  const save = async (a) => {
    try { await api.put(`/training/assets/${a.id}`, a); toast.success("Asset saved"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/training/assets/${id}`); toast.success("Deleted"); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const add = async () => {
    if (!draft.title) { toast.error("Title required"); return; }
    try { await api.post("/training/assets", draft); toast.success("Asset added"); setDraft(BLANK); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-sm border border-dashed border-border p-4" data-testid="asset-add">
        <Label className="overline">Add asset</Label>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <Input data-testid="asset-new-title" placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <select data-testid="asset-new-category" className="rounded-sm border border-border bg-background px-3 text-sm" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
            {CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <Input className="sm:col-span-2" placeholder="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          <Input className="sm:col-span-2" placeholder="File URL (leave blank for 'coming soon')" value={draft.file_url} onChange={(e) => setDraft({ ...draft, file_url: e.target.value })} />
          <label className="flex items-center gap-2 text-sm"><Switch checked={draft.per_rep_merge} onCheckedChange={(v) => setDraft({ ...draft, per_rep_merge: v })} /> Personalized per rep</label>
          <div className="flex justify-end"><Button data-testid="asset-add-btn" onClick={add} className="gap-1.5"><Plus size={15} /> Add</Button></div>
        </div>
      </div>
      {assets.map((a) => <AssetRow key={a.id} asset={a} onSave={save} onDelete={del} />)}
    </div>
  );
}

function AssetRow({ asset, onSave, onDelete }) {
  const [a, setA] = useState(asset);
  return (
    <div className="rounded-sm border border-border bg-card p-3" data-testid={`asset-edit-${asset.id}`}>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input value={a.title} onChange={(e) => setA({ ...a, title: e.target.value })} />
        <select className="rounded-sm border border-border bg-background px-3 text-sm" value={a.category} onChange={(e) => setA({ ...a, category: e.target.value })}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
        <Input className="sm:col-span-2" value={a.description} onChange={(e) => setA({ ...a, description: e.target.value })} />
        <Input className="sm:col-span-2" placeholder="File URL" value={a.file_url} onChange={(e) => setA({ ...a, file_url: e.target.value })} />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm"><Switch checked={a.per_rep_merge} onCheckedChange={(v) => setA({ ...a, per_rep_merge: v })} /> Personalized</label>
        <div className="flex gap-2">
          <Button size="sm" data-testid={`asset-save-${asset.id}`} onClick={() => onSave(a)} className="gap-1.5"><FloppyDisk size={14} /> Save</Button>
          <Button size="sm" variant="outline" data-testid={`asset-delete-${asset.id}`} onClick={() => onDelete(asset.id)}><Trash size={14} /></Button>
        </div>
      </div>
    </div>
  );
}
